import { useState, useEffect } from 'react';
import http from '../../api/client';
import type { SignupState } from './types';
import type { User } from '../../api/types';

/** Temporary pre-auth data stored in sessionStorage (only user_id) */
export const SIGNUP_USER_ID_KEY = 'signup_user_id';

/** Optional UI-only step persistence so refresh keeps user on same screen */
export const SIGNUP_STEP_KEY = 'signup_step';

/**
 * The React Router `location.key` stored while the user is on the signup page.
 * Browsers preserve window.history.state (and therefore location.key) across
 * F5 full-page refreshes, but React Router assigns a brand-new key on every
 * fresh client-side navigation.  Comparing the stored key with the current key
 * at mount time is therefore a reliable way to tell "refresh" from "new visit".
 */
export const SIGNUP_LOCATION_KEY = 'signup_location_key';

export const saveSignupStep = (step: number) => {
  try {
    sessionStorage.setItem(SIGNUP_STEP_KEY, String(step));
  } catch {
    /* ignore */
  }
};

export const loadSignupStep = (): number | null => {
  try {
    const val = sessionStorage.getItem(SIGNUP_STEP_KEY);
    if (val) {
      const n = parseInt(val, 10);
      if (n >= 1 && n <= 5) return n;
    }
  } catch {
    /* ignore */
  }
  return null;
};

export const saveTempUserId = (userId: number) => {
  try {
    sessionStorage.setItem(SIGNUP_USER_ID_KEY, String(userId));
  } catch {
    /* ignore */
  }
};

export const loadTempUserId = (): number | null => {
  try {
    // Primary: dedicated key written immediately after phone verification
    const val = sessionStorage.getItem(SIGNUP_USER_ID_KEY);
    if (val) {
      const n = parseInt(val, 10);
      if (!isNaN(n)) return n;
    }
    // Fallback: form draft written on every field change — more likely to exist
    const draft = sessionStorage.getItem('signup_form_draft');
    if (draft) {
      const parsed = JSON.parse(draft);
      if (parsed?.user_id) return Number(parsed.user_id);
    }
  } catch {
    /* ignore */
  }
  return null;
};

export const clearTempUserId = () => {
  try {
    sessionStorage.removeItem(SIGNUP_USER_ID_KEY);
    sessionStorage.removeItem('signup_form_draft');
    sessionStorage.removeItem(SIGNUP_STEP_KEY);
    sessionStorage.removeItem(SIGNUP_LOCATION_KEY);
  } catch {
    /* ignore */
  }
};

/** True if user has in-progress signup data (survives refresh) */
export const hasPersistedSignupProgress = (): boolean =>
  !!loadTempUserId() || !!sessionStorage.getItem('signup_form_draft');

/**
 * Persist the current React Router location.key while the user is on the signup
 * page.  Called on every render so the stored key always matches the live one.
 */
export const storeSignupLocationKey = (key: string) => {
  try {
    sessionStorage.setItem(SIGNUP_LOCATION_KEY, key);
  } catch {
    /* ignore */
  }
};

/**
 * Returns true if the given location.key matches the one stored from the
 * previous visit to this page (i.e. the user pressed F5 / browser-refresh)
 * AND there is in-progress signup data to restore.
 */
export const isSignupRefresh = (locationKey: string): boolean => {
  try {
    const stored = sessionStorage.getItem(SIGNUP_LOCATION_KEY);
    return stored === locationKey && hasPersistedSignupProgress();
  } catch {
    return false;
  }
};

interface SignupStatusResponse {
  step: number;
  profile_completion_level: number;
  user_id: number;
  role: 'user' | 'doctor';
  first_name: string;
  middle_name: string;
  last_name: string;
  phone_number: string;
  country_code: string;
  phone_verified: boolean;
  email: string;
  email_verified: boolean;
  email_verification_skipped?: boolean;
  dob?: string;
  gender?: string;
  is_invited?: boolean;
  relation?: string | null;
  fields_locked?: string[];
}

/**
 * True when the patient chose “skip email for now” (or legacy equivalent).
 * Used to hide “Back” on basic profile so users aren’t sent to the email step after phone re-login.
 */
export function userSkippedEmailVerification(
  user: User | null | undefined,
): boolean {
  if (!user) return false;
  const level = user.profile_completion_level ?? 0;
  return (
    user.email_verification_skipped === true ||
    (user.email_verification_skipped == null &&
      typeof user.email === 'string' &&
      user.email.startsWith('temp_') &&
      level >= 2)
  );
}

/**
 * Pure function: derive signup step from an authenticated user.
 * Phase 2 (post-auth) — no API call needed.
 *
 * For patients who skipped email: Profile -> Medical -> Dosha first, then Email at end.
 * This lets users resume their remaining onboarding steps after relogin.
 * For doctors: email before profile (no skip option). Medical + dosha are not part of signup.
 */
export function stepFromUser(user: User): number | 'COMPLETED' {
  const level = user.profile_completion_level ?? 0;
  const rawV = user.verification_email as unknown;
  const emailVerified = rawV === true || rawV === 1 || rawV === 'true';
  const explicitlySkipped = userSkippedEmailVerification(user);
  const phoneVerified = user.phone_verified === true;

  if (user.role === 'doctor') {
    if (!emailVerified) return 2;
    if (level < 3) return 3;
    return 'COMPLETED';
  }

  // Patient rules:
  // 1) Not skipped (or not yet reached skip decision) => return to Step 2.
  // 2) Explicitly skipped => continue profile/medical/dosha, ask email at end.
  if (!emailVerified && !explicitlySkipped) return 2;

  // Patient: continue incomplete profile/medical/dosha first.
  if (level < 3) return 3;
  if (level < 4) return 4;
  if (level < 5) return 5;
  // Once profile/medical/dosha are done, bring back email verification.
  if (!emailVerified) return 2;
  // Google OAuth etc.: phone was skipped in wizard — require verification before leaving signup.
  if (!phoneVerified) return 5;
  return 'COMPLETED';
}

interface UseSignupStepResult {
  /** null while loading from backend */
  step: number | null;
  /** Partial state fetched from backend (pre-auth only) */
  restoredState: Partial<SignupState> | null;
  loading: boolean;
}

/** Read and parse the local draft safely */
const loadDraft = (): Partial<SignupState> | null => {
  try {
    const raw = sessionStorage.getItem('signup_form_draft');
    if (raw) return JSON.parse(raw) as Partial<SignupState>;
  } catch {
    /* ignore */
  }
  return null;
};

/**
 * Derive a signup step purely from the local form draft.
 * This is used as an instant, offline fallback when the backend is unreachable
 * or the /signup-status API has not yet been deployed to the running server.
 *
 *   phone not verified          → step 1
 *   phone verified, no token   → step 2 (credentials)
 *   email verified              → step 3 (profile)
 */
const stepFromDraft = (draft: Partial<SignupState>): number => {
  if (!draft.phone_verified) return 1;
  if (draft.email_verified) return 3;
  return 2;
};

/**
 * Derives the correct signup step to display.
 *
 * Phase 1 (pre-auth):  user is null.
 *   - When isFresh: always step 1, no draft/restore.
 *   - Otherwise: reads local form draft, optionally confirms with /auth/signup-status/:user_id.
 * Phase 2 (post-auth): user is available; derive step from user.profile_completion_level
 *                      and user.verification_email directly — no API call.
 */
/** Only fields that affect signup step — avoids re-running the resolver when `user` is a new object reference with the same progress. */
function userStepFingerprint(user: User | null): string {
  if (!user) return '';
  const rawV = user.verification_email as unknown;
  const emailVerified =
    rawV === true || rawV === 1 || rawV === 'true' ? '1' : '0';
  const skip = userSkippedEmailVerification(user) ? '1' : '0';
  return [
    user.id,
    user.role,
    user.profile_completion_level ?? 0,
    emailVerified,
    user.phone_verified === true ? '1' : '0',
    skip,
  ].join('|');
}

export function useSignupStep(
  user: User | null,
  tempUserId: number | null,
  isFresh = false,
): UseSignupStepResult {
  const [step, setStep] = useState<number | null>(null);
  const [restoredState, setRestoredState] =
    useState<Partial<SignupState> | null>(null);
  const [loading, setLoading] = useState(true);

  const loggedInKey = user ? userStepFingerprint(user) : '';

  useEffect(() => {
    let cancelled = false;

    const resolve = async () => {
      // ─── Phase 2: logged in ───────────────────────────────────────
      if (user) {
        const derived = stepFromUser(user);
        const nextStep =
          derived === 'COMPLETED' ? (user.role === 'doctor' ? 3 : 5) : derived;
        if (!cancelled) {
          setStep((prev) => (prev === nextStep ? prev : nextStep));
          setRestoredState(null);
          setLoading(false);
        }
        return;
      }

      // ─── Phase 1: pre-auth ────────────────────────────────────────
      // When fresh: always step 1, no draft/restore.
      if (isFresh) {
        if (!cancelled) {
          setStep(1);
          setRestoredState(null);
          setLoading(false);
        }
        return;
      }

      // UI-only step persistence (optional), then draft, then backend
      const savedStep = loadSignupStep();
      const draft = loadDraft();
      const draftStep = draft ? stepFromDraft(draft) : 1;
      // Be conservative and resume to the furthest progressed local step.
      // `signup_step` can be stale (e.g. if the user advanced but a write failed),
      // while `signup_form_draft` reflects the latest verified flags.
      const localStep =
        savedStep != null ? Math.max(savedStep, draftStep) : draftStep;

      if (!tempUserId) {
        // No user_id at all — use draft step (covers the case where user_id
        // was written to draft but SIGNUP_USER_ID_KEY was somehow missed)
        if (!cancelled) {
          setStep(localStep);
          if (draft && localStep > 1) {
            setRestoredState({
              user_id: draft.user_id ?? null,
              role: draft.role,
              first_name: draft.first_name,
              middle_name: draft.middle_name,
              last_name: draft.last_name,
              phone_number: draft.phone_number,
              country_code: draft.country_code,
              phone_verified: draft.phone_verified,
              email: draft.email,
              email_verified: draft.email_verified,
            });
          } else {
            setRestoredState(null);
          }
          setLoading(false);
        }
        return;
      }

      // tempUserId present → show local step immediately, then confirm with backend
      if (!cancelled) {
        setStep(localStep);
        if (draft) {
          setRestoredState({
            user_id: draft.user_id ?? null,
            role: draft.role,
            first_name: draft.first_name,
            middle_name: draft.middle_name,
            last_name: draft.last_name,
            phone_number: draft.phone_number,
            country_code: draft.country_code,
            phone_verified: draft.phone_verified,
            email: draft.email,
            email_verified: draft.email_verified,
          });
        }
        setLoading(false);
      }

      // Fire-and-forget backend confirmation — overrides local step if reachable
      try {
        // Include invite_token so the backend can resolve fields_locked in one round-trip
        const inviteToken =
          typeof localStorage !== 'undefined'
            ? localStorage.getItem('pending_invite_token')
            : null;
        const url = inviteToken
          ? `/auth/signup-status/${tempUserId}?invite_token=${encodeURIComponent(inviteToken)}`
          : `/auth/signup-status/${tempUserId}`;

        const res = await http.get<SignupStatusResponse>(url);
        if (!cancelled) {
          const data = res.data;
          setStep(data.step);
          setRestoredState({
            user_id: data.user_id,
            role: data.role,
            first_name: data.first_name,
            middle_name: data.middle_name,
            last_name: data.last_name,
            phone_number: data.phone_number,
            country_code: data.country_code,
            phone_verified: data.phone_verified,
            email: data.email,
            email_verified: data.email_verified,
            email_verification_skipped: data.email_verification_skipped,
            dob: data.dob || '',
            gender: data.gender || '',
            is_invited: data.is_invited ?? false,
            relation: data.relation ?? '',
            fields_locked: data.fields_locked ?? [],
          });
        }
      } catch {
        // Backend unreachable or route not yet deployed — keep local step.
        // Do NOT clear session data; the user should stay on their current step.
        console.warn(
          '[useSignupStep] /signup-status call failed; using local draft step:',
          localStep,
        );
      }
    };

    resolve();
    return () => {
      cancelled = true;
    };
    // `loggedInKey` replaces `user` in deps so a new object reference with the same step state does not re-run this effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `user` read from render closure when loggedInKey changes
  }, [loggedInKey, tempUserId, isFresh]);

  return { step, restoredState, loading };
}
